'use client';

import type { MaintenanceWindow } from '@magicschool/business-logic/site-configs';
import { Alert } from '@magicschool/spellbook';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { MILLISECONDS_IN_HOUR } from 'util/date';
import { storage } from '@/features/storage';

export type MaintenanceBannerContentProps = {
  maintenance: MaintenanceWindow | null;
  preview?: boolean;
  onDismiss?: () => void;
};

const localStorageKey = (labelId: string | null | undefined, borkedFunction: string | null | undefined) =>
  `${labelId || 'site'}-${borkedFunction || 'generic'}-maintenance-closed`;

const coerceDate = (value: Date | string | number | null | undefined): Date | null => {
  if (value === null || value === undefined) {
    return null;
  }

  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const normalizeMaintenanceWindow = (maintenance: MaintenanceWindow): MaintenanceWindow | null => {
  const startDateTime = coerceDate(maintenance.startDateTime);
  if (!startDateTime) {
    return null;
  }

  return {
    ...maintenance,
    startDateTime,
    endDateTime: coerceDate(maintenance.endDateTime),
    showWarningBannerDateTime: coerceDate(maintenance.showWarningBannerDateTime),
    optionalBorkedDay: coerceDate(maintenance.optionalBorkedDay),
  };
};

const getHoursMessage = (showWarningBannerDateTime: Date | null | undefined, startDateTime: Date | null | undefined) => {
  if (!startDateTime || !showWarningBannerDateTime) return null;

  const now = new Date();
  const hoursUntilMaintenance = Math.floor((startDateTime.getTime() - now.getTime()) / MILLISECONDS_IN_HOUR);

  if (!hoursUntilMaintenance) return null;
  return hoursUntilMaintenance > 1 ? (
    <FormattedMessage id="maintenance.site-maintenance.hours" values={{ hours: hoursUntilMaintenance }} />
  ) : (
    <FormattedMessage id="maintenance.site-maintenance.less_than_one_hour" />
  );
};

export const MaintenanceBannerContent = ({ maintenance, preview = false, onDismiss }: MaintenanceBannerContentProps) => {
  const intl = useIntl();
  const [open, setOpen] = useState(true);

  if (!open) return null;
  if (!maintenance) return null;

  const normalizedMaintenance = normalizeMaintenanceWindow(maintenance);
  if (!normalizedMaintenance) return null;

  const { labelId, borkedFunction, startDateTime, endDateTime, showWarningBannerDateTime, mode, borkedPeriod, optionalBorkedDay } =
    normalizedMaintenance;

  if (!preview) {
    const closedKey = localStorageKey(labelId, borkedFunction);
    const closed = Number(storage.getItem(closedKey));
    if (startDateTime.valueOf() === closed) return null;
  }

  const now = new Date();
  const shouldBeVisible =
    endDateTime && endDateTime.getTime() <= now.getTime() ? false : (showWarningBannerDateTime || startDateTime).getTime() < now.getTime();
  if (!preview && !shouldBeVisible) return null;

  const hoursMessage = getHoursMessage(showWarningBannerDateTime, startDateTime);

  const siteMaintenanceTitleId = hoursMessage
    ? endDateTime
      ? 'maintenance.site-maintenance.title'
      : 'maintenance.site-maintenance.title-sentence'
    : endDateTime
      ? 'maintenance.site-maintenance.title-no-countdown'
      : 'maintenance.site-maintenance.title-no-countdown-sentence';

  const handleClose = () => {
    if (preview) return;

    onDismiss?.();
    const closedKey = localStorageKey(labelId, borkedFunction);
    storage.setItem(closedKey, String(startDateTime.valueOf()));
    setOpen(false);
  };

  return (
    // Announced by PageBannerRegion; the admin preview sits outside one, so it keeps Alert's own role.
    <Alert
      role={preview ? undefined : 'presentation'}
      onDismiss={preview ? undefined : handleClose}
      status="warning"
      variant="banner"
      text={{ dismissButtonLabel: intl.formatMessage({ id: 'close', defaultMessage: 'Close' }) }}
    >
      <Alert.Content>
        {mode === 'serviceInconsistent' && borkedFunction && borkedPeriod && (
          <FormattedMessage
            id="maintenance.service-planned-bork.inconsistent-through"
            values={{
              borkedFunction,
              b: (text) => <b key="borked-function">{text}</b>,
              borkedPeriod: intl.formatMessage({ id: `maintenance.service-planned-bork.inconsistent-through-${borkedPeriod}` }),
              optionalDay: optionalBorkedDay
                ? `${optionalBorkedDay.toLocaleDateString(intl.locale, { month: 'long', day: 'numeric' })}`
                : '',
            }}
          />
        )}
        {mode === 'siteMaintenance' && (
          <>
            <FormattedMessage
              id={siteMaintenanceTitleId}
              values={{
                startTime: startDateTime.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }),
                ...(hoursMessage ? { hoursUntilMaintenance: hoursMessage } : {}),
                b: (text) => <b key="maintenance">{text}</b>,
              }}
            />
            {endDateTime && (
              <FormattedMessage
                id="maintenance.site-maintenance.until"
                values={{
                  endTime: endDateTime.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }),
                  b: (text) => <b key="maintenance-until">{text}</b>,
                }}
              />
            )}
          </>
        )}
        {labelId && (
          <>
            {(mode === 'siteMaintenance' || (mode === 'serviceInconsistent' && borkedFunction && borkedPeriod)) && ' '}
            <FormattedMessage
              id={labelId}
              values={borkedFunction ? { borkedFunction: borkedFunction, b: (text) => <b key="borked-function">{text}</b> } : undefined}
            />
          </>
        )}
      </Alert.Content>
    </Alert>
  );
};
